import React, { useState, useEffect, useCallback, useRef } from 'react';
import './OrderDetails.css';
import { db } from '../../firebase';
import { collection, addDoc, query, where, getDocs, doc, updateDoc, deleteDoc, orderBy, onSnapshot } from 'firebase/firestore';
import { formatPrice } from '../../utils';
import { useAuth } from '../../hooks/useAuth';
import OrderForm from './OrderForm';
import { 
  ORDER_STATUS, 
  ORDER_STATUS_LABELS, 
  PRODUCT_TYPES, 
  PAYMENT_TYPES, 
  PAYMENT_TYPE_LABELS 
} from '../../constants';

// Merkezi Yardımcı Fonksiyonlar
import {
  getInitialOrderState,
  calculateUsedM2,
  calculateTotalPrice,
  calculateDiscountedPrice,
  calculateCapsulePrice,
  calculateStorZebraUsedM2,
  calculateStorZebraTotalPrice,
  calculateJaluziUsedM2,
  calculateJaluziTotalPrice,
  calculateAksesuarTotalPrice,
  calculateKatlamaliPerdeUsedM,
  calculateKatlamaliPerdeTotalPrice,
  calculateDikeyPerdeTotalPrice,
  calculateTotalCost,
  calculatePileDensity,
  calculatePliseBatchTotals,
  calculateRustikTotalPrice,
  calculateRaySistemiUsedMeter,
  calculateRustikUsedCm
} from './subcomponents/OrderDetailsUtils';

// Alt Bileşenler
import OrderDetailsHeader from './subcomponents/OrderDetailsHeader';
import OrderDetailsList from './subcomponents/OrderDetailsList';
import OrderDetailsSummary from './subcomponents/OrderDetailsSummary';
import OrderDetailsPDF from './subcomponents/OrderDetailsPDF';
import OrderDetailsModals from './subcomponents/OrderDetailsModals';
import OrderDetailsToast from './subcomponents/OrderDetailsToast';

const OrderDetails = ({ customerId, customerName, isEditMode, onCustomerSaved, initialStatus, onOrderStatusChange: propOnOrderStatusChange, onPricingChange }) => {
  const { user, permissions, isMontajci } = useAuth();

  const [orders, setOrders] = useState([]);
  const [isAddingOrder, setIsAddingOrder] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [selectedProductType, setSelectedProductType] = useState('');
  const [newOrder, setNewOrder] = useState(getInitialOrderState());
  const [isLoading, setIsLoading] = useState(false);
  const [orderStatus, setOrderStatus] = useState(initialStatus || ORDER_STATUS.NEW_ORDER);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [paidAmount, setPaidAmount] = useState('0');
  const [isPaymentCompleted, setIsPaymentCompleted] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [newPaymentAmount, setNewPaymentAmount] = useState('');
  const [newPaymentType, setNewPaymentType] = useState(PAYMENT_TYPES.CASH);
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  const [genelIskontoYuzde, setGenelIskontoYuzde] = useState('');
  const [vadeliYuzde, setVadeliYuzde] = useState('');
  const [manualToplamOdeme, setManualToplamOdeme] = useState('');
  const [isSavingVadeli, setIsSavingVadeli] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [cancelPaymentConfirm, setCancelPaymentConfirm] = useState(null);
  const [showSystemConfirm, setShowSystemConfirm] = useState(false);
  const [pileModels, setPileModels] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const formRef = useRef(null);

  // Toast göster
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
  };

  // Müşteri bilgilerini çek
  useEffect(() => {
    if (!customerId || customerId.startsWith('temp_')) return;

    const unsubscribe = onSnapshot(doc(db, 'customers', customerId), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setCustomerInfo(data);
        setOrderStatus(data.orderStatus || ORDER_STATUS.NEW_ORDER);
        setPaidAmount(String(data.paidAmount || 0));
        setIsPaymentCompleted(data.isPaymentCompleted || false);
        setPaymentHistory(data.paymentHistory || []);
        setGenelIskontoYuzde(data.genelIskontoYuzde || '');
        setVadeliYuzde(data.vadeliYuzde || '');
        setManualToplamOdeme(
          data.manualToplamOdeme !== undefined && data.manualToplamOdeme !== null && data.manualToplamOdeme !== ''
            ? String(data.manualToplamOdeme)
            : ''
        );
      }
    });

    return () => unsubscribe();
  }, [customerId]);

  // Pile modellerini çek
  useEffect(() => {
    const fetchPileModels = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'pileModels'));
        const models = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPileModels(models);
      } catch (error) {
        console.error('Pile modelleri çekilirken hata:', error);
      }
    };
    fetchPileModels();
  }, []);

  // Siparişleri çek
  const fetchOrders = async () => {
    if (!customerId) return;
    
    if (customerId.startsWith('temp_')) {
      return; 
    }

    try {
      setIsLoading(true);
      const q = query(
        collection(db, 'orders'),
        where('customerId', '==', customerId)
      );
      const querySnapshot = await getDocs(q);
      const ordersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // İstemci tarafında sıralama - En yeni en üstte
      ordersData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setOrders(ordersData);
    } catch (error) {
      console.error('Siparişler çekilirken hata:', error);
      showToast('Siparişler yüklenemedi.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [customerId]);

  // Sipariş durumu değişikliği
  const onOrderStatusChange = async (newStatus) => {
    // Montajcı yalnızca "Teslim Edildi" yapabilir
    if (isMontajci && newStatus !== ORDER_STATUS.DELIVERED) {
      showToast('Montajcı rolü yalnızca "Teslim Edildi" durumuna geçiş yapabilir.', 'error');
      return;
    }

    if (!customerId || customerId.startsWith('temp_')) {
      setOrderStatus(newStatus);
      if (propOnOrderStatusChange) {
        propOnOrderStatusChange(newStatus);
      }
      return;
    }

    try {
      setIsLoading(true);
      await updateDoc(doc(db, 'customers', customerId), {
        orderStatus: newStatus,
        updatedAt: new Date()
      });
      setOrderStatus(newStatus);
      
      // Üst bileşene durum değişikliğini bildir
      if (propOnOrderStatusChange) {
        propOnOrderStatusChange(newStatus);
      }
      
      showToast('Sipariş durumu güncellendi.');
    } catch (error) {
      console.error('Durum güncellenirken hata:', error);
      showToast('Durum güncellenirken bir hata oluştu.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // İskonto/Vade Kaydet
  const handleSavePricingFields = async () => {
    if (!customerId || customerId.startsWith('temp_')) return;

    try {
      setIsSavingVadeli(true);
      const isk = parseFloat(genelIskontoYuzde) || 0;
      const vad = parseFloat(vadeliYuzde) || 0;

      const currentIskontolu = totalAmount * (1 - isk / 100);
      const currentVadeli = totalAmount * (1 + vad / 100);

      let computedCurrentToplam;
      if (vad > 0) {
        computedCurrentToplam = currentVadeli;
      } else if (isk > 0) {
        computedCurrentToplam = currentIskontolu;
      } else if (discountedTotalAmount !== totalAmount) {
        computedCurrentToplam = discountedTotalAmount; // Ürün indirimli toplam fallback
      } else {
        computedCurrentToplam = totalAmount;
      }

      // Manuel düzenlenmiş Genel Toplam (Net) varsa onu kullan
      const manualNum = parseFloat(manualToplamOdeme);
      const useManual =
        manualToplamOdeme !== '' && !isNaN(manualNum) && manualNum >= 0;
      const currentToplamOdeme = useManual ? manualNum : computedCurrentToplam;

      const currentRemaining = Math.max(0, currentToplamOdeme - paidAmountNum);

      await updateDoc(doc(db, 'customers', customerId), {
        genelIskontoYuzde: isk,
        vadeliYuzde: vad,
        manualToplamOdeme: useManual ? manualNum : null,
        remainingAmount: currentRemaining,
        isPaymentCompleted: currentRemaining <= 0.01, // Floating point safety
        updatedAt: new Date()
      });
      const updatedData = {
        id: customerId,
        ...customerInfo,
        genelIskontoYuzde: isk,
        vadeliYuzde: vad,
        manualToplamOdeme: useManual ? manualNum : null,
        remainingAmount: currentRemaining,
        isPaymentCompleted: currentRemaining <= 0.01
      };

      if (onCustomerSaved) onCustomerSaved(updatedData);
      showToast('Fiyat bilgileri kaydedildi.');
    } catch (error) {
      console.error('Fiyat bilgileri kaydedilirken hata:', error);
      showToast('Hata oluştu.', 'error');
    } finally {
      setIsSavingVadeli(false);
    }
  };

  const handleGenelIskontoYuzdeChange = (value) => {
    setGenelIskontoYuzde(value);
    // İskonto değişince manuel Genel Toplam override'ını sıfırla
    setManualToplamOdeme('');
    if (onPricingChange) onPricingChange({ genelIskontoYuzde: value, vadeliYuzde, manualToplamOdeme: '' });
  };

  const handleVadeliYuzdeChange = (value) => {
    setVadeliYuzde(value);
    // Vade değişince manuel Genel Toplam override'ını sıfırla
    setManualToplamOdeme('');
    if (onPricingChange) onPricingChange({ genelIskontoYuzde, vadeliYuzde: value, manualToplamOdeme: '' });
  };

  const handleManualToplamOdemeChange = (value) => {
    // Sadece sayı ve nokta/virgül kabul et
    if (value === '' || /^\d*[.,]?\d*$/.test(value)) {
      // Virgülü noktaya çevir (Türkçe input desteği)
      const normalized = value.replace(',', '.');
      setManualToplamOdeme(normalized);
      // Üst bileşene (CustomerList) anlık olarak bildir; "Fiyat Bilgilerini Kaydet"
      // butonuna basılmadan "İşlemi Tamamla" kullanılabilmesi için gerekli
      if (onPricingChange) onPricingChange({ genelIskontoYuzde, vadeliYuzde, manualToplamOdeme: normalized });
    }
  };

  const handleResetManualToplamOdeme = () => {
    setManualToplamOdeme('');
    if (onPricingChange) onPricingChange({ genelIskontoYuzde, vadeliYuzde, manualToplamOdeme: '' });
  };

  // Kopyalama sonrası hesaplamaları tetikleyen useEffect
  useEffect(() => {
    if (isAddingOrder && (selectedProductType === 'tul' || selectedProductType === 'kanat') && newOrder.width && newOrder.height && newOrder.pileModel) {
      const updatedOrder = { ...newOrder };
      const density = parseFloat(newOrder.pileDensity) || 0;
      const usedM2 = calculateUsedM2(newOrder.width, newOrder.height, newOrder.pileModel, density > 0 ? density : null);
      updatedOrder.usedM2 = usedM2.toFixed(2);

      if (updatedOrder.usedM2 && updatedOrder.unitPrice) {
        const totalPrice = calculateTotalPrice(
          updatedOrder.usedM2,
          updatedOrder.unitPrice,
          updatedOrder.extraPrice,
          updatedOrder.pileModel,
          updatedOrder.capsulePrice
        );
        const quantity = parseFloat(updatedOrder.quantity) || 1;
        updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);
      }

      if (updatedOrder.totalPrice && updatedOrder.discount) {
        const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
        updatedOrder.discountedPrice = discountedPrice.toFixed(2);
      }
      setNewOrder(updatedOrder);
    }

    if (isAddingOrder && selectedProductType === 'plise') {
      const { items, totalPrice, discountedPrice } = calculatePliseBatchTotals(
        newOrder.pliseItems,
        newOrder.pliseProduct1Price,
        newOrder.pliseProduct2Price,
        newOrder.mounting,
        newOrder.discount,
        newOrder.quantity
      );
      if (totalPrice !== newOrder.totalPrice) {
        setNewOrder(prev => ({ ...prev, pliseItems: items, totalPrice, discountedPrice }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddingOrder, selectedProductType]);

  const handleProductTypeChange = (productType) => {
    setSelectedProductType(productType);
    setNewOrder(getInitialOrderState());
  };

  const handleFieldChange = (field, value) => {
    setNewOrder(prevOrder => {
      const updatedOrder = { ...prevOrder, [field]: value };

      if (field === 'motorType' && !value) {
        updatedOrder.motorPrice = 0;
        updatedOrder.motorPriceCurrency = '';
      }

      // Tül ve Kanat
      if (selectedProductType === 'tul' || selectedProductType === 'kanat') {
        if (field === 'pileModel') {
          let density = 0;
          if (pileModels.length > 0) {
            const selectedModel = pileModels.find(m => m.modelName === value);
            density = selectedModel?.density || calculatePileDensity(value);
          } else {
            density = calculatePileDensity(value);
          }
          updatedOrder.pileDensity = density.toString();
          if (updatedOrder.width && updatedOrder.height) {
            const usedM2 = calculateUsedM2(updatedOrder.width, updatedOrder.height, value, density);
            updatedOrder.usedM2 = usedM2.toFixed(2);
          }
        }

        if (['width', 'height', 'unitPrice', 'extraPrice', 'quantity', 'discount', 'pileDensity', 'capsuleUnitPrice', 'pileModel'].includes(field)) {
          const density = parseFloat(updatedOrder.pileDensity) || 0;
          const usedM2 = calculateUsedM2(updatedOrder.width, updatedOrder.height, updatedOrder.pileModel, density);
          updatedOrder.usedM2 = usedM2.toFixed(2);
          
          if (updatedOrder.pileModel === 'Kapsül Pile' && updatedOrder.capsuleUnitPrice) {
            updatedOrder.capsulePrice = calculateCapsulePrice(updatedOrder.usedM2 || updatedOrder.width, updatedOrder.capsuleUnitPrice);
          } else if (updatedOrder.pileModel !== 'Kapsül Pile') {
            updatedOrder.capsulePrice = '';
          }

          const totalPrice = calculateTotalPrice(
            updatedOrder.usedM2,
            updatedOrder.unitPrice,
            updatedOrder.extraPrice,
            updatedOrder.pileModel,
            updatedOrder.capsulePrice
          );
          const qty = parseFloat(updatedOrder.quantity) || 1;
          updatedOrder.totalPrice = (totalPrice * qty).toFixed(2);
          updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
        }
      }

      // Stor ve Zebra
      if (selectedProductType === 'stor' || selectedProductType === 'zebra') {
        if (['width', 'height'].includes(field)) {
          updatedOrder.usedM2 = calculateStorZebraUsedM2(updatedOrder.width, updatedOrder.height).toFixed(2);
        }
        const isZebra = selectedProductType === 'zebra';
        const extras = isZebra ? {
          jumbo: updatedOrder.hasZebraJumbo ? updatedOrder.zebraJumboAmount : 0,
          altEtekDilim: updatedOrder.hasZebraAltEtekDilim ? updatedOrder.zebraAltEtekDilimAmount : 0,
          kapaliKasa: updatedOrder.hasZebraKapaliKasa ? updatedOrder.zebraKapaliKasaAmount : 0
        } : {
          jumbo: updatedOrder.hasStorJumbo ? updatedOrder.storJumboAmount : 0,
          altEtekDilim: updatedOrder.hasStorAltEtekDilim ? updatedOrder.storAltEtekDilimAmount : 0,
          kapaliKasa: updatedOrder.hasStorKapaliKasa ? updatedOrder.storKapaliKasaAmount : 0,
          altEtekLazer: updatedOrder.hasStorAltEtekLazer ? updatedOrder.storAltEtekLazerAmount : 0,
          dekoratifLama: updatedOrder.hasStorDekoratifLama ? updatedOrder.storDekoratifLamaAmount : 0,
          dekoratifKasa: updatedOrder.hasStorDekoratifKasa ? updatedOrder.storDekoratifKasaAmount : 0,
          altEtekBeyazIs: updatedOrder.hasStorAltEtekBeyazIs ? updatedOrder.storAltEtekBeyazIsAmount : 0
        };

        const totalPricePreAdet = calculateStorZebraTotalPrice(
          updatedOrder.usedM2, updatedOrder.unitPrice, updatedOrder.width, extras, updatedOrder.motorPrice, updatedOrder.mounting
        );
        updatedOrder.totalPrice = (totalPricePreAdet * (parseFloat(updatedOrder.quantity) || 1)).toFixed(2);
        updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
      }
      // Jaluzi
      if (selectedProductType === 'ahsapJaluzi' || selectedProductType === 'metalJaluzi') {
        if (['width', 'height'].includes(field)) {
          updatedOrder.usedM2 = calculateJaluziUsedM2(updatedOrder.width, updatedOrder.height).toFixed(2);
        }
        const totalPricePreAdet = calculateJaluziTotalPrice(
          updatedOrder.usedM2, updatedOrder.unitPrice, updatedOrder.mounting, 
          updatedOrder.motorPrice, updatedOrder.zigilAmount || updatedOrder.metalZigilAmount || 0,
          updatedOrder.ikiCamArasiAmount || 0
        );
        updatedOrder.totalPrice = (totalPricePreAdet * (parseFloat(updatedOrder.quantity) || 1)).toFixed(2);
        updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
      }

      // Dikey Perde
      if (['dikeyPerde', 'dikeyPvc', 'dikeyStor'].includes(selectedProductType)) {
        if (['width', 'height'].includes(field)) {
          updatedOrder.usedM2 = calculateJaluziUsedM2(updatedOrder.width, updatedOrder.height).toFixed(2);
        }
        const totalPricePreAdet = calculateDikeyPerdeTotalPrice(
          updatedOrder.usedM2, updatedOrder.unitPrice, updatedOrder.dikeyFonAmount || 0,
          updatedOrder.mounting, updatedOrder.motorPrice
        );
        updatedOrder.totalPrice = (totalPricePreAdet * (parseFloat(updatedOrder.quantity) || 1)).toFixed(2);
        updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
      }

      // Katlamalı Perde
      if (selectedProductType === 'katlamaliPerde') {
        if (field === 'width') {
          updatedOrder.usedM = calculateKatlamaliPerdeUsedM(updatedOrder.width).toFixed(2);
        }
        updatedOrder.totalPrice = calculateKatlamaliPerdeTotalPrice(
          updatedOrder.usedM, updatedOrder.fabricPrice, updatedOrder.mechanismPrice,
          updatedOrder.accessories, updatedOrder.mounting, updatedOrder.motorPrice,
          updatedOrder.quantity
        ).toFixed(2);
        updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
      }

      // Aksesuar
      if (selectedProductType === 'aksesuar') {
        const uPrice = parseFloat(updatedOrder.unitPrice) || 0;
        const qty = parseFloat(updatedOrder.quantity) || 1;
        const usedM = parseFloat(updatedOrder.usedM) || 1;
        
        let base = uPrice * qty;
        if (updatedOrder.birimTipi === 'metre') {
          base = uPrice * qty * usedM;
        }
        
        updatedOrder.totalPrice = (base + (parseFloat(updatedOrder.mounting) || 0)).toFixed(2);
        updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
      }

      // Ray Sistemi
      if (selectedProductType === 'raySistemi') {
        if (['width', 'olcuTamamlama'].includes(field)) {
          updatedOrder.usedMeter = calculateRaySistemiUsedMeter(updatedOrder.width, updatedOrder.olcuTamamlama).toFixed(2);
        }
        if (updatedOrder.olcuTamamlama) {
          const base = (parseFloat(updatedOrder.usedMeter) || 0) * (parseFloat(updatedOrder.unitPrice) || 0);
          updatedOrder.totalPrice = ((base + (parseFloat(updatedOrder.mounting) || 0)) * (parseFloat(updatedOrder.quantity) || 1)).toFixed(2);
        } else {
          updatedOrder.totalPrice = '0.00';
        }
        updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
      }

      // Rustik
      if (selectedProductType === 'rustik') {
        const usedCm = calculateRustikUsedCm(updatedOrder.width);
        updatedOrder.usedM2 = usedCm;
        const usedMeters = usedCm / 100;

        if (field === 'width') {
          // Ayak sayısı otomatik seçimi
          if (usedCm <= 200) {
            updatedOrder.ayakAdeti = 2;
          } else if (usedCm < 440) {
            updatedOrder.ayakAdeti = 3;
          } else {
            updatedOrder.ayakAdeti = 4;
          }
        }

        // Ray seçenekleri tutarlarını güncelle
        if (field === 'width' || field === 'rayOptions') {
          if (Array.isArray(updatedOrder.rayOptions)) {
            updatedOrder.rayOptions = updatedOrder.rayOptions.map(opt => ({
              ...opt,
              total: ((parseFloat(opt.price) || 0) * usedMeters).toFixed(2)
            }));
          }
        }
        updatedOrder.totalPrice = calculateRustikTotalPrice(
          updatedOrder.unitPrice, 
          updatedOrder.quantity, 
          updatedOrder.ayakAdeti, 
          updatedOrder.ekAyakSayisi, 
          updatedOrder.ayakUcreti, 
          updatedOrder.mounting,
          updatedOrder.rayOptions,
          updatedOrder.accessories,
          updatedOrder.usedM2
        ).toFixed(2);
        updatedOrder.discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount).toFixed(2);
      }

      // Plise
      if (selectedProductType === 'plise') {
        const { items, totalPrice, discountedPrice } = calculatePliseBatchTotals(
          updatedOrder.pliseItems, updatedOrder.pliseProduct1Price, updatedOrder.pliseProduct2Price, 
          updatedOrder.mounting, updatedOrder.discount, updatedOrder.quantity
        );
        updatedOrder.pliseItems = items;
        updatedOrder.totalPrice = totalPrice;
        updatedOrder.discountedPrice = discountedPrice;
      }

      // Diğer tipler için genel maliyet hesaplama (unitCost varsa)
      if (['unitCost', 'quantity', 'usedM2'].includes(field)) {
        const tCost = calculateTotalCost(updatedOrder.usedM2, updatedOrder.unitCost);
        updatedOrder.totalCost = (tCost * (parseFloat(updatedOrder.quantity) || 1)).toFixed(2);
      }

      return updatedOrder;
    });
  };

  const handleAddOrder = async (e, forceSave = false) => {
    if (e) e.preventDefault();
    if (!customerId) return;

    // Stor/Zebra için sistem seçimi kontrolü
    if (!forceSave && (selectedProductType === 'stor' || selectedProductType === 'zebra')) {
      const isSystemSelected = selectedProductType === 'zebra' 
        ? (newOrder.hasZebraJumbo || newOrder.hasZebraAltEtekDilim || newOrder.hasZebraKapaliKasa)
        : (newOrder.hasStorJumbo || newOrder.hasStorAltEtekDilim || newOrder.hasStorKapaliKasa || 
           newOrder.hasStorAltEtekLazer || newOrder.hasStorDekoratifLama || newOrder.hasStorDekoratifKasa || 
           newOrder.hasStorAltEtekBeyazIs);

      if (!isSystemSelected) {
        setShowSystemConfirm(true);
        return;
      }
    }

    try {
      const orderData = { 
        customerId, 
        productType: selectedProductType, 
        ...newOrder, 
        updatedAt: new Date() 
      };

      if (editingOrderId) {
        await updateDoc(doc(db, 'orders', editingOrderId), orderData);
      } else {
        orderData.createdAt = new Date();
        await addDoc(collection(db, 'orders'), orderData);

        // Yeni sipariş eklenirken fiyat bilgilerini (iskonto/vade) müşteri belgesine kaydet
        if (!customerId.startsWith('temp_')) {
          const isk = parseFloat(genelIskontoYuzde) || 0;
          const vad = parseFloat(vadeliYuzde) || 0;

          // Mevcut siparişlere yeni siparişi dahil ederek toplamları hesapla
          const newOrderTotal = parseFloat(newOrder.totalPrice || 0);
          const newOrderDiscounted = parseFloat(newOrder.discountedPrice || newOrder.totalPrice || 0);
          const newTotalAmount = totalAmount + newOrderTotal;
          const newDiscountedTotal = discountedTotalAmount + newOrderDiscounted;

          const newIskontolu = newTotalAmount * (1 - isk / 100);
          const newVadeli = newTotalAmount * (1 + vad / 100);

          let newToplamOdeme;
          if (vad > 0) {
            newToplamOdeme = newVadeli;
          } else if (isk > 0) {
            newToplamOdeme = newIskontolu;
          } else if (newDiscountedTotal !== newTotalAmount) {
            newToplamOdeme = newDiscountedTotal;
          } else {
            newToplamOdeme = newTotalAmount;
          }

          // Yeni sipariş eklenince mevcut manuel Genel Toplam (Net) düzeltmesini sıfırla
          // çünkü temel toplam değişti
          setManualToplamOdeme('');

          const newRemaining = Math.max(0, newToplamOdeme - paidAmountNum);

          await updateDoc(doc(db, 'customers', customerId), {
            genelIskontoYuzde: isk,
            vadeliYuzde: vad,
            manualToplamOdeme: null,
            remainingAmount: newRemaining,
            isPaymentCompleted: newRemaining <= 0.01,
            updatedAt: new Date()
          });

          // Üst bileşene (CustomerList) anlık güncelleme bildir
          if (onCustomerSaved) {
            onCustomerSaved({
              id: customerId,
              ...customerInfo,
              genelIskontoYuzde: isk,
              vadeliYuzde: vad,
              manualToplamOdeme: null,
              remainingAmount: newRemaining,
              isPaymentCompleted: newRemaining <= 0.01
            });
          }
        }
      }

      fetchOrders();
      setIsAddingOrder(false);
      setEditingOrderId(null);
      setNewOrder(getInitialOrderState());
      showToast('Sipariş başarıyla kaydedildi.');
    } catch (error) {
      console.error('Hata:', error);
      showToast('Kaydedilemedi.', 'error');
    }
  };

  const handleEditOrder = (order) => {
    setSelectedProductType(order.productType);
    setNewOrder({ ...order });
    setEditingOrderId(order.id);
    setIsAddingOrder(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleCopyOrder = (order) => {
    setSelectedProductType(order.productType);
    const copied = { ...order };
    delete copied.id;
    delete copied.createdAt;
    setNewOrder(copied);
    setEditingOrderId(null);
    setIsAddingOrder(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleDeleteConfirm = (order) => setDeleteConfirm(order);
  const handleDeleteOrder = async () => {
    try {
      await deleteDoc(doc(db, 'orders', deleteConfirm.id));
      setDeleteConfirm(null);

      // Sipariş silinince mevcut manuel Genel Toplam (Net) düzeltmesini sıfırla,
      // çünkü temel toplam değişti
      if (manualToplamOdeme !== '' && customerId && !customerId.startsWith('temp_')) {
        try {
          await updateDoc(doc(db, 'customers', customerId), {
            manualToplamOdeme: null,
            updatedAt: new Date()
          });
          setManualToplamOdeme('');
        } catch (e) {
          console.error('Manuel toplam sıfırlanamadı:', e);
        }
      }

      fetchOrders();
      showToast('Sipariş silindi.');
    } catch (error) {
      showToast('Hata oluştu.', 'error');
    }
  };

  const handleCancelPayment = async (payment) => {
    try {
      const updatedHistory = paymentHistory.filter(p => p.id !== payment.id);
      const totalPaid = updatedHistory.reduce((sum, p) => sum + parseFloat(p.amount), 0);
      const newRemaining = Math.max(0, toplamOdeme - totalPaid);
      
      await updateDoc(doc(db, 'customers', customerId), {
        paymentHistory: updatedHistory,
        paidAmount: totalPaid,
        remainingAmount: newRemaining,
        isPaymentCompleted: newRemaining <= 0.01
      });
      const updatedData = {
        id: customerId,
        ...customerInfo,
        paymentHistory: updatedHistory,
        paidAmount: totalPaid,
        remainingAmount: newRemaining,
        isPaymentCompleted: newRemaining <= 0.01
      };
      
      setCancelPaymentConfirm(null);
      if (onCustomerSaved) onCustomerSaved(updatedData);
      showToast('Ödeme iptal edildi.');
    } catch (error) {
      showToast('İşlem başarısız.', 'error');
    }
  };

  const handleAddPayment = async () => {
    const amount = parseFloat(newPaymentAmount);
    if (!amount) return;

    if (amount + paidAmountNum > toplamOdeme + 0.01) {
      showToast(`Ödeme miktarı kalan tutardan (${formatPrice(remainingAmount)} ₺) fazla olamaz.`, 'error');
      return;
    }

    try {
      setIsAddingPayment(true);
      const newPayment = {
        id: Date.now().toString(),
        amount,
        type: newPaymentType,
        date: new Date().toISOString()
      };
      const updatedHistory = [...paymentHistory, newPayment];
      const totalPaid = updatedHistory.reduce((sum, p) => sum + p.amount, 0);
      const newRemaining = Math.max(0, toplamOdeme - totalPaid);

      await updateDoc(doc(db, 'customers', customerId), {
        paymentHistory: updatedHistory,
        paidAmount: totalPaid,
        remainingAmount: newRemaining,
        isPaymentCompleted: newRemaining <= 0.01
      });
      const updatedData = {
        id: customerId,
        ...customerInfo,
        paymentHistory: updatedHistory,
        paidAmount: totalPaid,
        remainingAmount: newRemaining,
        isPaymentCompleted: newRemaining <= 0.01
      };

      setNewPaymentAmount('');
      if (onCustomerSaved) onCustomerSaved(updatedData);
      showToast('Ödeme eklendi.');
    } catch (error) {
      showToast('Ödeme eklenemedi.', 'error');
    } finally {
      setIsAddingPayment(false);
    }
  };

  const onToggleAddOrder = () => {
    const nextState = !isAddingOrder;
    setIsAddingOrder(nextState);
    if (nextState) {
      setEditingOrderId(null);
      setSelectedProductType('');
      setNewOrder(getInitialOrderState());
    }
  };

  const handlePaymentCompletedChange = async (checked) => {
    try {
      setIsPaymentCompleted(checked);
      await updateDoc(doc(db, 'customers', customerId), {
        isPaymentCompleted: checked,
        updatedAt: new Date()
      });
      
      if (onCustomerSaved) {
        onCustomerSaved({
          ...customerInfo,
          id: customerId,
          isPaymentCompleted: checked
        });
      }
      showToast(checked ? 'Ödeme tamamlandı olarak işaretlendi.' : 'Ödeme durumu güncellendi.');
    } catch (error) {
      console.error('Ödeme durumu güncellenirken hata:', error);
      showToast('Hata oluştu.', 'error');
    }
  };

  // Finansal Değişkenler
  const totalAmount = orders.reduce((sum, o) => sum + parseFloat(o.totalPrice || 0), 0);
  const discountedTotalAmount = orders.reduce((sum, o) => sum + parseFloat(o.discountedPrice || o.totalPrice || 0), 0);

  const iskYuzde = parseFloat(genelIskontoYuzde) || 0;
  const vadYuzde = parseFloat(vadeliYuzde) || 0;

  // İskontolu Toplam: her zaman ham Ara Toplam üzerine iskonto uygulanır
  const iskontoluToplam = totalAmount * (1 - iskYuzde / 100);
  // Vadeli Toplam: her zaman ham Ara Toplam üzerine vade farkı eklenir
  const vadeliToplam = totalAmount * (1 + vadYuzde / 100);

  // Genel Toplam (Net) hiyerarşisi:
  // Vadeli > İskontolu > Ürün İndirimli > Ara Toplam
  let computedToplamOdeme;
  if (vadYuzde > 0) {
    computedToplamOdeme = vadeliToplam;
  } else if (iskYuzde > 0) {
    computedToplamOdeme = iskontoluToplam;
  } else if (discountedTotalAmount !== totalAmount) {
    computedToplamOdeme = discountedTotalAmount;
  } else {
    computedToplamOdeme = totalAmount;
  }

  // Kullanıcı küsuratlı tutarları yuvarlamak için Genel Toplam (Net)'i elle düzenleyebilir
  const manualToplamOdemeNum = parseFloat(manualToplamOdeme);
  const hasManualToplamOdeme =
    manualToplamOdeme !== '' &&
    !isNaN(manualToplamOdemeNum) &&
    manualToplamOdemeNum >= 0;
  const toplamOdeme = hasManualToplamOdeme ? manualToplamOdemeNum : computedToplamOdeme;

  const paidAmountNum = parseFloat(paidAmount) || 0;
  const remainingAmount = isPaymentCompleted ? 0 : Math.max(0, toplamOdeme - paidAmountNum);

  return (
    <div className="order-details">
      <OrderDetailsHeader
        customerName={customerName}
        customerInfo={customerInfo}
        orderStatus={orderStatus}
        onOrderStatusChange={onOrderStatusChange}
        isLoading={isLoading}
        isMontajci={isMontajci}
        permissions={permissions}
        isAddingOrder={isAddingOrder}
        onToggleAddOrder={onToggleAddOrder}
      />

      <div ref={formRef}>
        {isAddingOrder && (
          <OrderForm
            isAddingOrder={isAddingOrder}
            editingOrderId={editingOrderId}
            selectedProductType={selectedProductType}
            newOrder={newOrder}
            isLoading={isLoading}
            customerInfo={customerInfo}
            customerId={customerId}
            onProductTypeChange={handleProductTypeChange}
            onFieldChange={handleFieldChange}
            onSubmit={handleAddOrder}
            onCancel={() => setIsAddingOrder(false)}
            pileModels={pileModels}
          />
        )}
      </div>

      <OrderDetailsList
        orders={orders}
        permissions={permissions}
        handleEditOrder={handleEditOrder}
        handleCopyOrder={handleCopyOrder}
        handleDeleteConfirm={handleDeleteConfirm}
        isLoading={isLoading}
      />

      {orders.length > 0 && (
        <>
          <OrderDetailsSummary
            totalAmount={totalAmount}
            discountedTotalAmount={discountedTotalAmount}
            iskontoluToplam={iskontoluToplam}
            vadeliToplam={vadeliToplam}
            toplamOdeme={toplamOdeme}
            computedToplamOdeme={computedToplamOdeme}
            manualToplamOdeme={manualToplamOdeme}
            handleManualToplamOdemeChange={handleManualToplamOdemeChange}
            handleResetManualToplamOdeme={handleResetManualToplamOdeme}
            remainingAmount={remainingAmount}
            paidAmount={paidAmount}
            isPaymentCompleted={isPaymentCompleted}
            genelIskontoYuzde={genelIskontoYuzde}
            vadeliYuzde={vadeliYuzde}
            handleGenelIskontoYuzdeChange={handleGenelIskontoYuzdeChange}
            handleVadeliYuzdeChange={handleVadeliYuzdeChange}
            handlePaymentCompletedChange={handlePaymentCompletedChange}
            isMontajci={isMontajci}
            isLoading={isLoading}
            isSavingPayment={isSavingPayment}
            isEditMode={isEditMode}
            paymentHistory={paymentHistory}
            setCancelPaymentConfirm={setCancelPaymentConfirm}
            handleSavePricingFields={handleSavePricingFields}
            isSavingVadeli={isSavingVadeli}
            // Payment Form Props
            customerId={customerId}
            orders={orders}
            newPaymentType={newPaymentType}
            setNewPaymentType={setNewPaymentType}
            newPaymentAmount={newPaymentAmount}
            setNewPaymentAmount={setNewPaymentAmount}
            isAddingPayment={isAddingPayment}
            handleAddPayment={handleAddPayment}
          />

          <OrderDetailsPDF
            customerInfo={customerInfo}
            orders={orders}
            totalAmount={totalAmount}
            discountedTotalAmount={discountedTotalAmount}
            genelIskontoYuzde={genelIskontoYuzde}
            vadeliYuzde={vadeliYuzde}
            paidAmount={paidAmount}
            remainingAmount={remainingAmount}
            manualToplamOdeme={manualToplamOdeme}
          />
        </>
      )}

      <OrderDetailsModals
        deleteConfirm={deleteConfirm}
        setDeleteConfirm={setDeleteConfirm}
        handleDeleteOrder={handleDeleteOrder}
        cancelPaymentConfirm={cancelPaymentConfirm}
        setCancelPaymentConfirm={setCancelPaymentConfirm}
        handleCancelPayment={handleCancelPayment}
        showSystemConfirm={showSystemConfirm}
        setShowSystemConfirm={setShowSystemConfirm}
        handleAddOrder={handleAddOrder}
      />

      <OrderDetailsToast
        toast={toast}
        setToast={setToast}
      />
    </div>
  );
};

export default OrderDetails;